// Parse a framework SVG string into its viewBox, inner markup, labeled parts, and guide overlay.
export function parseFramework(svgString) {
  if (!svgString) return { viewBox: "0 0 800 600", inner: "", parts: [], guides: "" };
  const doc = new DOMParser().parseFromString(svgString, "image/svg+xml");
  const svg = doc.querySelector("svg");
  if (!svg) return { viewBox: "0 0 800 600", inner: "", parts: [], guides: "" };
  let viewBox = svg.getAttribute("viewBox");
  if (!viewBox) {
    const w = svg.getAttribute("width") || 800;
    const h = svg.getAttribute("height") || 600;
    viewBox = `0 0 ${parseFloat(w)} ${parseFloat(h)}`;
  }
  const parts = [];
  const seen = new Set();
  const partShapes = {};
  svg.querySelectorAll('[id]').forEach((el) => {
    const id = el.getAttribute("id");
    if (id && id.toLowerCase().startsWith("part") && !seen.has(id)) {
      seen.add(id);
      const nameSrc = el.getAttribute("inkscape:label") || el.getAttribute("data-name") || id;
      const { duplicates } = parseDuplicateRule(nameSrc);
      parts.push({ id, label: humanize(nameSrc), duplicates: duplicates || null });
      const clone = el.cloneNode(true);
      ["id", "fill", "stroke", "stroke-width", "style"].forEach((a) => clone.removeAttribute(a));
      partShapes[id] = new XMLSerializer().serializeToString(clone);
    }
  });
  // Collect "guide" layers, strip their ids, and remove them from the base layer
  // so they can be rendered on top of everything in the canvas.
  const guideEls = Array.from(svg.querySelectorAll('[id]')).filter(
    (el) => el.getAttribute("id").toLowerCase().startsWith("guide")
  );
  const guides = guideEls
    .map((el) => {
      const clone = el.cloneNode(true);
      clone.removeAttribute("id");
      return new XMLSerializer().serializeToString(clone);
    })
    .join("");
  guideEls.forEach((el) => el.parentNode && el.parentNode.removeChild(el));

  // Detect the calibration reference: a shape named "measurement" (by id, data-name,
  // or inkscape:label). It represents a 1x1 inch square. Strip it from the base layer
  // so it is invisible on the canvas AND excluded from every export (inner has no copy).
  const isMeasure = (el) => {
    const n = (el.getAttribute("id") || el.getAttribute("data-name") || el.getAttribute("inkscape:label") || "").toLowerCase();
    return n === "measurement" || n.startsWith("measurement");
  };
  let measurementEl = null;
  for (const el of Array.from(svg.querySelectorAll("*"))) {
    if (isMeasure(el)) { measurementEl = el; break; }
  }
  let measurement = "";
  if (measurementEl) {
    const clone = measurementEl.cloneNode(true);
    clone.removeAttribute("id");
    measurement = new XMLSerializer().serializeToString(clone);
    measurementEl.parentNode && measurementEl.parentNode.removeChild(measurementEl);
  }

  return { viewBox, inner: svg.innerHTML, parts, guides, partShapes, measurement };
}

// Parse an optional "--duplicates: Source Panel--" rule out of a layer name.
// Returns the clean name (rule removed) and the referenced source label (or null).
export function parseDuplicateRule(name) {
  if (!name) return { clean: "", duplicates: null };
  const re = /--\s*(?:duplicates?|dup)\s*:\s*(.+?)\s*--/i;
  const m = name.match(re);
  return { clean: name.replace(re, "").trim(), duplicates: m ? m[1].trim() : null };
}

export function humanize(id) {
  const { clean } = parseDuplicateRule(id || "");
  const label = clean.replace(/^part[-_]?/i, "").replace(/[-_]/g, " ").trim();
  return label ? label.replace(/\b\w/g, (c) => c.toUpperCase()) : clean;
}

// Normalize a label/name for case-insensitive duplicate-source matching.
export function normalizeLabel(s) {
  return humanize(s || "").toLowerCase().replace(/\s+/g, " ").trim();
}

// Format inches: up to 2 decimals, trailing zeros trimmed (1.20 -> "1.2", 1.00 -> "1").
export function fmtInch(n) {
  return n == null || isNaN(n) ? "" : (Math.round(n * 100) / 100).toString();
}